<script setup>
import { ref, onMounted, computed } from 'vue';
import TreeTable from 'primevue/treetable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import ButtonGroup from "primevue/buttongroup";
import ConfirmDeleteDialog from '@/components/common/ConfirmDeleteDialog.vue';
import { useDocumentTypesStore } from '@/stores/documentTypes.js';

const emit = defineEmits(['edit-document', 'edit-file', 'delete-document', 'delete-file', 'configure-permissions']);

const documentTypesStore = useDocumentTypesStore();
const showDeleteDialog = ref(false);
const itemToDelete = ref(null);

const loading = computed(() =>
  documentTypesStore.loading.documentTypes || documentTypesStore.loading.fileTypes
);

// Преобразуем дерево в формат для TreeTable
const treeNodes = computed(() => {
  return convertToTreeNodes(documentTypesStore.combinedTree);
});

function convertToTreeNodes(items) {
  return items.map(item => ({
    key: String(item.id),
    data: {
      ...item, // Копируем весь объект
      name: item.name || item.item_name,
      extension: item.extension || '—',
      role_ids: item.role_ids || [],
    },
    children: item.children && item.children.length > 0
      ? convertToTreeNodes(item.children)
      : []
  }));
}

onMounted(async () => {
  await documentTypesStore.loadAll();
});

function handleEdit(node) {
  if (node.data.entity_type === 'document') {
    const docType = documentTypesStore.getDocumentTypeById(node.data.id);
    emit('edit-document', docType);
  } else if (node.data.entity_type === 'file') {
    const fileType = documentTypesStore.getFileTypeById(node.data.id);
    emit('edit-file', fileType);
  }
}

function handleDelete(node) {

  let itemData = null;

  if (node.data.entity_type === 'document') {
    const docType = documentTypesStore.getDocumentTypeById(node.data.id);

    if (docType) {
      itemData = { ...docType, entity_type: 'document' };
    } else {
      // Если не нашли в store, используем данные из node
      itemData = { ...node.data, entity_type: 'document' };
    }
  } else if (node.data.entity_type === 'file') {
    const fileType = documentTypesStore.getFileTypeById(node.data.id);

    if (fileType) {
      itemData = { ...fileType, entity_type: 'file' };
    } else {
      // Если не нашли в store, используем данные из node
      itemData = { ...node.data, entity_type: 'file' };
    }
  }


  if (!itemData || !itemData.id) {
    console.error('Cannot delete: no item data or ID');
    return;
  }

  itemToDelete.value = itemData;
  showDeleteDialog.value = true;
}

function confirmDelete() {

  if (!itemToDelete.value) return;

  if (itemToDelete.value.entity_type === 'document') {
    emit('delete-document', itemToDelete.value);
  } else if (itemToDelete.value.entity_type === 'file') {
    emit('delete-file', itemToDelete.value);
  }

  showDeleteDialog.value = false;
  itemToDelete.value = null;
}

function handleConfigurePermissions(node) {

  let itemData = null;

  if (node.data.entity_type === 'document') {
    const docType = documentTypesStore.getDocumentTypeById(node.data.id);
    if (docType) {
      itemData = { ...docType, entity_type: 'document' };
    } else {
      itemData = { ...node.data, entity_type: 'document' };
    }
  } else if (node.data.entity_type === 'file') {
    const fileType = documentTypesStore.getFileTypeById(node.data.id);
    if (fileType) {
      itemData = { ...fileType, entity_type: 'file' };
    } else {
      itemData = { ...node.data, entity_type: 'file' };
    }
  }

  if (!itemData || !itemData.id) {
    console.error('Cannot configure permissions: no item data or ID');
    return;
  }

  emit('configure-permissions', itemData);
}

function getTypeLabel(entityType) {
  if (entityType === 'document') return 'Документ';
  if (entityType === 'file') return 'Файл';
  return '—';
}
</script>

<template>
  <div class="h-full flex flex-col">
    <TreeTable
      :value="treeNodes"
      :loading="loading"
      scrollable
      scroll-height="flex"
      class="flex-1"
    >
      <Column field="name" header="Название" expander style="min-width: 300px"></Column>
      <Column field="entity_type" header="Тип" style="min-width: 150px">
        <template #body="{ node }">
          {{ getTypeLabel(node.data.entity_type) }}
        </template>
      </Column>
      <Column header="Действия" style="min-width: 280px">
        <template #body="{ node }">
          <div v-if="node.data.entity_type !== 'root'" class="flex gap-2">
            <ButtonGroup>
              <Button
                icon="pi pi-key"
                severity="secondary"
                size="small"
                rounded
                text
                @click="handleConfigurePermissions(node)"
                v-tooltip="'Настройка прав'"
              />
              <Button
                icon="pi pi-pencil"
                severity="secondary"
                size="small"
                rounded
                text
                @click="handleEdit(node)"
                v-tooltip="'Редактировать'"
              />
              <Button
                icon="pi pi-trash"
                severity="warn"
                size="small"
                rounded
                text
                @click="handleDelete(node)"
                v-tooltip="'Удалить'"
              />
            </ButtonGroup>
          </div>
        </template>
      </Column>
    </TreeTable>

    <!-- Диалог подтверждения удаления -->
    <ConfirmDeleteDialog
      v-model:visible="showDeleteDialog"
      :document-name="itemToDelete?.entity_type === 'document' ? (itemToDelete?.item_name || itemToDelete?.name) : ''"
      :facility-name="itemToDelete?.entity_type === 'file' ? (itemToDelete?.item_name || itemToDelete?.name) : ''"
      @confirm="confirmDelete"
    />
  </div>
</template>

<style scoped>
</style>
